import { useMemo, useCallback } from 'react';
import { useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import axiosClient, { axiosClientAdmin } from '@/lib/auth';
import { buildDismissPreferenceKey } from '../keys';

interface DismissState {
  isDismissed: boolean;
  isLoading: boolean;
  dismiss: () => void;
}

/**
 * Hook to manage dismiss states for multiple announcements using TanStack Query.
 * Uses useQueries to fetch all states in parallel and useMutation for dismissals.
 */
export function useAnnouncementDismissStates(
  announcementIds: string[],
  displayType: 'modal' | 'banner'
): Record<string, DismissState> {
  const { user, isAuthenticatedMonitor } = useAuthStore();
  const queryClient = useQueryClient();
  const client = isAuthenticatedMonitor ? axiosClient : axiosClientAdmin;

  // Fetch all dismiss states in parallel using useQueries
  const queries = useQueries({
    queries: announcementIds.map((id) => ({
      queryKey: ['userPreference', buildDismissPreferenceKey(id, displayType)],
      queryFn: async () => {
        const key = buildDismissPreferenceKey(id, displayType);
        const response = await client.get(`/api/UserPreferences/${key}`);
        return response.data.preference?.value === 'true';
      },
      enabled: Boolean(user?.id),
      staleTime: 1000 * 60 * 5, // 5 minutes
    })),
  });

  // Mutation for dismissing an announcement
  const dismissMutation = useMutation({
    mutationFn: async (announcementId: string) => {
      const key = buildDismissPreferenceKey(announcementId, displayType);
      await client.post('/api/UserPreferences', {
        userId: user?.id,
        key,
        value: 'true',
      });
      return announcementId;
    },
    onMutate: async (announcementId: string) => {
      const key = buildDismissPreferenceKey(announcementId, displayType);
      const queryKey = ['userPreference', key];

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousValue = queryClient.getQueryData(queryKey);

      // Optimistically update to dismissed
      queryClient.setQueryData(queryKey, true);

      return { previousValue, queryKey };
    },
    onError: (_err, _announcementId, context) => {
      // Rollback on error
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousValue);
      }
      console.error('[Announcements] Error dismissing announcement');
    },
  });

  // Create dismiss functions for each announcement
  const createDismiss = useCallback(
    (id: string) => () => {
      dismissMutation.mutate(id);
    },
    [dismissMutation]
  );

  // Build the result map
  const result = useMemo(() => {
    const states: Record<string, DismissState> = {};

    announcementIds.forEach((id, index) => {
      const query = queries[index];
      states[id] = {
        isDismissed: query?.data ?? false,
        isLoading: query?.isLoading ?? true,
        dismiss: createDismiss(id),
      };
    });

    return states;
  }, [announcementIds, queries, createDismiss]);

  return result;
}
